package dev.lingvo.site.ds

import kotlin.math.pow
import kotlin.math.round

/** Numeric normalization shared by text commits, keyboard steps and pointer scrubbing. */
class LNumberFieldModel(
    private val step: Double = 1.0,
    private val bigStep: Double = 10.0,
    private val min: Double? = null,
    private val max: Double? = null,
    private val decimals: Int = 0,
) {
    init {
        require(step.isFinite() && step > 0.0)
        require(bigStep.isFinite() && bigStep > 0.0)
        require(min == null || min.isFinite())
        require(max == null || max.isFinite())
        require(min == null || max == null || min <= max)
        require(decimals in 0..10)
    }

    /** Parses a complete finite number, then rounds and clamps it to the field's limits. */
    fun commit(raw: String): Double? = raw.trim().toDoubleOrNull()?.takeIf(Double::isFinite)?.let(::normalize)

    /** Formats the canonical value, retaining extra bound precision; null leaves Mixed visible. */
    fun display(value: Double?): String = value?.takeIf(Double::isFinite)?.let {
        val normalized = normalize(it).toString()
        if ('e' in normalized.lowercase()) normalized else {
            val whole = normalized.substringBefore('.')
            val fraction = normalized.substringAfter('.', "").trimEnd('0').padEnd(decimals, '0')
            if (fraction.isEmpty()) whole else "$whole.$fraction"
        }
    }.orEmpty()

    /** Applies one arrow or button step; a mixed field starts from zero within its bounds. */
    fun stepped(value: Double?, direction: Int, shift: Boolean): Double =
        normalize((value?.takeIf(Double::isFinite) ?: 0.0) + direction * if (shift) bigStep else step)

    /** Maps horizontal CSS pixels from the start of a scrub into normal or Shift steps. */
    fun scrubbed(value: Double?, pixels: Double, shift: Boolean): Double =
        normalize((value?.takeIf(Double::isFinite) ?: 0.0) + pixels * if (shift) bigStep else step)

    private fun normalize(value: Double): Double {
        // Bounds can carry more precision than ordinary steps. Keep an already clamped endpoint
        // canonical so displaying and recommitting it cannot drift back onto the decimal grid.
        if (value == min || value == max) return if (value == 0.0) 0.0 else value
        val scale = 10.0.pow(decimals)
        val scaled = value * scale
        val rounded = if (scaled.isFinite()) round(scaled) / scale else value
        val bounded = rounded.coerceIn(min ?: -Double.MAX_VALUE, max ?: Double.MAX_VALUE)
        return if (bounded == 0.0) 0.0 else bounded
    }
}
